import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Btn } from './Btn'
import { SmartLink } from './SmartLink'
import { useSeo } from '../seo/useSeo'
import type { SeoSite } from '../seo/types'
import type { LinkTarget, NavLink } from './types'

export interface NotFoundVerse {
  text: string
  reference: string
  /** e.g. `WEB` — quote a public-domain translation, and keep the attribution. */
  translation: string
}

export interface NotFoundProps {
  logo: string
  /** The app's own name, beside the mark. */
  name: string
  code?: string
  title: string
  descr: string
  verse?: NotFoundVerse
  primaryCta: LinkTarget & { label: string }
  secondaryCta?: LinkTarget & { label: string }
  linksLabel: string
  /** Where else to go. Entries may point at another KKF app. */
  links: NavLink[]
  /**
   * This app's site identity, so the page can mark itself `noindex`.
   *
   * It matters more here than anywhere else. The SPA fallback answers every unmatched URL
   * with HTTP 200, so without this a mistyped or long-dead link is a page a search engine
   * is entitled to index — a soft 404, and one that would carry whatever title the
   * previous route left in the tab.
   */
  site?: SeoSite
}

/**
 * The not-found page, served by every app's unmatched route.
 *
 * Mounted **outside** `AppShell` on purpose, so it has no header, no footer and no
 * breadcrumb — a breadcrumb tells you where you are in a hierarchy, and this page is the
 * one place on a site that is nowhere. It stands alone: the mark, the verse, and a way
 * back.
 *
 * Being outside the shell also means nothing else resets the scroll position, so it does
 * that itself — a broken link followed from halfway down a page would otherwise land you
 * halfway down this one.
 */
export function NotFound({
  logo,
  name,
  code = '404',
  title,
  descr,
  verse,
  primaryCta,
  secondaryCta,
  linksLabel,
  links,
  site,
}: NotFoundProps) {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useSeo(site, {
    title: `${title} — ${name}`,
    description: descr,
    path: pathname,
    noindex: true,
  })

  return (
    <section className="ul-404">
      {/* The code is a watermark rather than a heading — decorative, so it is hidden
          from assistive tech, which gets the real heading below. */}
      <span className="ul-404-watermark" aria-hidden="true">
        {code}
      </span>

      <div className="ul-404-container">
        <SmartLink to={primaryCta.to} href={primaryCta.href} className="ul-404-brand">
          <img src={logo} alt={name} />
          <span className="ul-404-brand-name">{name}</span>
        </SmartLink>

        <h1 className="ul-404-title">{title}</h1>
        <p className="ul-404-descr">{descr}</p>

        {verse && (
          <figure className="ul-404-verse">
            <blockquote>{verse.text}</blockquote>
            <figcaption>
              {verse.reference} <span className="translation">({verse.translation})</span>
            </figcaption>
          </figure>
        )}

        <div className="ul-404-actions">
          <Btn {...(primaryCta.href ? { href: primaryCta.href } : { to: primaryCta.to ?? '/' })} icon="fast-forward-double-right-arrows-symbol">
            {primaryCta.label}
          </Btn>
          {/* `--2` is the design system's secondary button: white fill, ink-navy label. */}
          {secondaryCta && (
            <Btn
              {...(secondaryCta.href ? { href: secondaryCta.href } : { to: secondaryCta.to ?? '/' })}
              className="ul-btn--2"
              icon="fast-forward-double-right-arrows-symbol"
            >
              {secondaryCta.label}
            </Btn>
          )}
        </div>

        <nav className="ul-404-links" aria-label={linksLabel}>
          <span className="ul-404-links-label">{linksLabel}</span>
          <ul>
            {links.map((link) => (
              <li key={link.label}>
                <SmartLink to={link.to} href={link.href}>
                  {link.label}
                </SmartLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  )
}
